import {
  FileDetails,
  ListallCommentsResponse,
  ListTagsinaprojectResponse,
  TcModelAlignment,
} from '@data-access/connect/tc-tcps';
import { TcObjectType } from '@data-access/connect/tc-tcps';
import {
  FileFolderItem,
  isModelVisible,
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
  useProjectSettings,
} from '@mobile-mapping/providers';
import { UseQueryResult } from '@tanstack/solid-query';
import { useConnectProject } from '@trimble/connect-project-providers';
import { t } from '@trimble/i18n';
import { asError } from '@trimble/logging';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { useLogger } from '@trimble/solidjs-utils';
import {
  ModelDetailsContainer,
  ModelDetailsView,
} from '@trimble/ui-components/solidjs';
import { TrimBimCapabilityProvider } from '@trimble/web3d-providers';
import { Component, createEffect, createMemo, createSignal } from 'solid-js';

export interface ModelDetailsProps {
  projectExplorerUrl: string;
  model: FileFolderItem;
  onClose(): void;
}

export const ModelDetails: Component<ModelDetailsProps> = (props) => {
  const { tcTcpsApi } = useApiServices();
  const { formattingSettings } = useProjectSettings();
  const { selectedRegion, selectedProject } = useConnectProject();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const { dataState } = useDataContext();
  const logger = useLogger().getLogger('ModelDetails');

  const [isSavingPosition, setIsSavingPosition] = createSignal(false);

  const positionAlignmentQuery = createMemo(
    (): UseQueryResult<TcModelAlignment, Error> | undefined => {
      const region = selectedRegion();
      if (!region) return;

      return tcTcpsApi.getFileAlignmentQuery(
        region,
        props.model.id,
        props.model.versionId
      );
    }
  );

  const fileDetailsQuery = createMemo(
    (): UseQueryResult<FileDetails, Error> | undefined => {
      const region = selectedRegion();
      if (!region) return;

      return tcTcpsApi.getFileDetailsQuery(region, props.model.id);
    }
  );

  const commentsQuery = createMemo(
    (): UseQueryResult<ListallCommentsResponse, Error> | undefined => {
      const region = selectedRegion();
      if (!region) return;

      return tcTcpsApi.listAllCommentsQuery(
        region,
        props.model.id,
        TcObjectType.FILE
      );
    }
  );

  const tagsQuery = createMemo(
    (): UseQueryResult<ListTagsinaprojectResponse, Error> | undefined => {
      const region = selectedRegion();
      const projectId = selectedProject()?.id;
      if (!region || !projectId) return;

      return tcTcpsApi.listTagsQuery(
        region,
        projectId,
        props.model.id,
        TcObjectType.FILE
      );
    }
  );

  const fileVersionsQuery = createMemo(
    (): UseQueryResult<FileDetails[], Error> | undefined => {
      const region = selectedRegion();
      if (!region) return;

      return tcTcpsApi.getFileVersionsQuery(region, props.model.id);
    }
  );

  function getImageBlobUrl(thumbnailUrl: string): Promise<string> {
    const region = selectedRegion();
    if (!region) return Promise.resolve('');

    return tcTcpsApi.getImageBlobUrl(thumbnailUrl);
  }

  function getFileDownloadUrl(item: FileDetails): Promise<string> {
    const region = selectedRegion();
    if (!region || !item.id) return Promise.resolve('');

    return tcTcpsApi
      .getFileDownloadUrl(region, item.id, item.versionId ?? item.id)
      .then((data) => data.url);
  }

  async function savePositionAlignment(
    alignment: TcModelAlignment
  ): Promise<boolean> {
    const region = selectedRegion();
    if (!region) return false;

    setIsSavingPosition(true);
    try {
      await tcTcpsApi.saveFileAlignment(region, alignment);
      return true;
    } catch (error) {
      logger.error(
        'Failed to save model position alignment',
        {
          fileId: props.model.id,
          versionId: props.model.versionId,
        },
        asError(error)
      );
      NotificationEventBus.emit({
        type: 'error',
        message: t('shared.ui.modelPosition.saveFailed'),
      });
      return false;
    } finally {
      setIsSavingPosition(false);
    }
  }

  function applyAlignmentToVisiblePanes(alignment: TcModelAlignment): void {
    if (!isModelVisible(dataState, props.model.id)) {
      return;
    }

    for (const pane of capabilityRegistry.paneContainers()) {
      pane
        .get(TrimBimCapabilityProvider)()
        ?.applyModelAlignment(props.model.id, alignment);
    }
  }

  function revertPositionAlignment(): void {
    const alignment = positionAlignmentQuery()?.data;
    if (!alignment) {
      return;
    }

    applyAlignmentToVisiblePanes(alignment);
  }

  createEffect(
    () => {
      const alignment = positionAlignmentQuery()?.data;
      if (!alignment) {
        return;
      }

      applyAlignmentToVisiblePanes(alignment);
    },
    { defer: true }
  );

  return (
    <ModelDetailsContainer
      projectExplorerUrl={props.projectExplorerUrl}
      modelName={props.model.name}
      fileDetailsQuery={fileDetailsQuery()}
      commentsQuery={commentsQuery()}
      tagsQuery={tagsQuery()}
      fileVersionsQuery={fileVersionsQuery()}
      positionAlignment={positionAlignmentQuery()?.data}
      positionSaving={isSavingPosition()}
      formattingSettings={formattingSettings()}
      getImageBlobUrl={(thumbnailUrl) => getImageBlobUrl(thumbnailUrl)}
      getFileDownloadUrl={(item) => getFileDownloadUrl(item)}
      onViewChange={(view) => {
        if (view === ModelDetailsView.Position) {
          void positionAlignmentQuery()?.refetch();
        }
      }}
      onApplyAlignment={applyAlignmentToVisiblePanes}
      onRevertPosition={revertPositionAlignment}
      onSavePosition={savePositionAlignment}
      onClose={props.onClose}
    />
  );
};
